'use client';

import React, { useCallback } from 'react';
import { css } from 'styled-system/css';

import { Button } from '@shaojiang/components/ui/button';

import PanoramaCanvas from './PanoramaCanvas';
import PanoramaMinimap from './PanoramaMinimap';
import { Point } from './types';
import { floatEqual } from './utils';

const PANO_WIDTH = 19500;
const PANO_HEIGHT = 1758;

const images = [
  '/images/p/0.jpeg',
  '/images/p/1.jpeg',
  '/images/p/2.jpeg',
  '/images/p/3.jpeg',
  '/images/p/4.jpeg',
  '/images/p/5.jpeg',
  '/images/p/6.jpeg',
  '/images/p/7.jpeg',
  '/images/p/8.jpeg',
  '/images/p/9.jpeg',
];

function App() {
  const [rotating, setRotating] = React.useState(false);
  const [center, setCenter] = React.useState({ x: 0, y: PANO_HEIGHT / 2 });
  const [zoom, setZoom] = React.useState(0.5);
  const [pannable, setPannable] = React.useState(true);
  const [visibleCanvasWidth, setVisibleCanvasWidth] = React.useState(0);

  const onUpdate = useCallback(
    (canvasCenter: Point, canvasZoom: number) => {
      if (!floatEqual(canvasZoom, zoom)) {
        setZoom(canvasZoom);
      }
      if (floatEqual(canvasCenter.x, center.x) && floatEqual(canvasCenter.y, center.y)) {
        return;
      }
      setCenter(canvasCenter);
    },
    [zoom, setZoom, setCenter, center.x, center.y],
  );

  const onVisibleCanvasWidthChanged = useCallback(
    (width: number) => {
      setVisibleCanvasWidth(width);
    },
    [setVisibleCanvasWidth],
  );

  const onMinimapClick = useCallback(
    ({ x, y }: Point) => {
      setCenter({ x, y });
    },
    [setCenter],
  );

  return (
    <div>
      <div className={css({ display: 'flex', flexWrap: 'wrap', gap: '2' })}>
        <Button onClick={() => setPannable(!pannable)}>Toggle Pannable: {`${pannable}`}</Button>
        <Button onClick={() => setRotating(!rotating)}>Toggle Rotating: {`${rotating}`}</Button>
        <Button onClick={() => setCenter((c) => ({ x: 0, y: c.y }))}>Set Center X (0)</Button>
        <Button onClick={() => setCenter((c) => ({ x: 1950, y: c.y }))}>Set Center X (1950)</Button>
        <Button onClick={() => setCenter((c) => ({ x: 3900, y: c.y }))}>Set Center X (3900)</Button>
        <Button onClick={() => setZoom((z) => z * 1.1)}>Zoom In</Button>
        <Button onClick={() => setZoom((z) => z / 1.1)}>Zoom Out</Button>
      </div>
      <div className={css({ my: '6' })}>
        <PanoramaCanvas
          images={images}
          center={center}
          zoom={zoom}
          pannable={pannable}
          rotating={rotating}
          panoWidth={PANO_WIDTH}
          panoHeight={PANO_HEIGHT}
          canvasHeight={500}
          onUpdate={onUpdate}
          onVisibleCanvasWidthChanged={onVisibleCanvasWidthChanged}
        />
      </div>
      <div className={css({ my: '6' })}>
        <PanoramaMinimap
          images={images}
          center={center}
          panoWidth={PANO_WIDTH}
          panoHeight={PANO_HEIGHT}
          visiblePanoHeight={500 / zoom}
          visiblePanoWidth={visibleCanvasWidth / zoom}
          canvasHeight={80}
          onMinimapClick={onMinimapClick}
        />
      </div>
    </div>
  );
}

export default App;
